import { BaseCard } from '@/components/uiKit/Cards';
import CardTitle from '@/components/uiKit/Cards/CardTitle';
import {
  MaintenanceChart,
  MAINTENANCE_CHART_CONTEXT,
} from '@/modules/shared/Maintenance/MaintenanceChart/MaintenanceChart';
import { useAppFilters } from '@/hooks';
import React, { useRef } from 'react';

/**
 * `MaintenanceCard`
 *
 * A React component that renders a card-based wrapper around the `MaintenanceChart`
 * visualization, customized for the Sensor Fusion context.
 *
 * This component integrates:
 * - A stylized `BaseCard` container with consistent layout.
 * - A section title labeled "Maintenance".
 * - A `MaintenanceChart` component, passed down a component selection and a wrapper reference.
 *
 * ### Features:
 * - Responds to the globally selected component (via `useAppFilters()`).
 * - Supports flexible layout via Tailwind CSS and `BaseCard` customization.
 * - Can be extended to include time range props (commented out as placeholders).
 *
 * @returns JSX element rendering the maintenance summary chart in a card layout.
 */
interface MaintenanceCardProps {
  dateRange: any;
}
export default function MaintenanceCard({ dateRange }: MaintenanceCardProps) {
  const { selectedComponent: selectedComponentId } = useAppFilters();

  // Ref to the BaseCard wrapper — used for layout awareness or dimension syncing in the chart.
  const baseCardRef = useRef<any>();

  return (
    <BaseCard
      ref={baseCardRef}
      variant="wrapper"
      classNames={{
        card: 'flex flex-col grow min-h-0 w-full max-h-[130px] min-h-[130px]',
        content: 'min-h-0 rounded-lg overflow-hidden flex flex-col h-fit',
      }}
      title={<CardTitle text={'Maintenance'} />}
    >
      <MaintenanceChart
        wrapperRef={baseCardRef}
        context={MAINTENANCE_CHART_CONTEXT.SensorFusion}
        selectedComponent={selectedComponentId}
        // Optionally provide X-axis bounds from shared time range:
        xAxisMin={dateRange.from}
        xAxisMax={dateRange.to}
      />
    </BaseCard>
  );
}
